import React from 'react'
import Spacer from '@app/components/atoms/Spacer'
import RoundedButton from '@app/ui/rounded-button/RoundedButton'
import { useAxios } from '@common/hooks/useAxios'
import { useState, useMemo, useEffect } from 'react'
import * as S from './styled'
import { DropDownMenu } from '@app/ui/dropdown-menu/DropDownMenu'
import firebase from 'firebase'
import {
  EvaluationDoc,
  SourceCourse,
} from '../../../pages/CompositeCoursePage/CompositeCoursePage'
import { RatingTemplate } from '../../../types/Templates'
import { useSnackbar } from '@app/snackbar'
import useUser from '@common/hooks/useUser'
import FeatherIcon from '@ovaeasy/react-native-vector-icons/Feather'
import Spinner from '@app/evaluation/components/Spinner'
import DateTimeView from '../../atoms/DateTimeView'
import { LRClub } from '../../../types/Clubs'
import Modal, { ModalHeader, ModalFooter } from '@app/components/Modal'
import SlopeTableHeaderPreview from '@app/components/molecules/SlopeTableHeaderPreview/SlopeTableHeaderPreview'

interface Props {
  courses?: SourceCourse[]
  template?: RatingTemplate
  club: LRClub
  isOpen: boolean
  onClose: () => void
}

const MixedEvaluationModal = ({
  courses,
  template,
  club,
  isOpen,
  onClose,
}: Props) => {
  const generateAxios = useAxios('/ratings/mixed/generate')
  const [isGenerating, setIsGenerating] = useState(false)
  const [openSnackbar] = useSnackbar()
  const user = useUser()
  const [sources, setSources] = useState(
    courses?.map(() => ({
      evaluationId: '',
      evaluationName: '',
      ratingDate: '',
      manualVersion: '',
      revision: 0,
    })) || []
  )

  const [clubName, setClubName] = useState(club?.name?.toUpperCase())
  const [courseName, setCourseName] = useState(template?.name)
  const [title, setTitle] = useState('')
  const [ratingName, setRatingName] = useState('')
  const [formRatingDate, setFormRatingDate] = useState('')

  const updateSource = (courseIndex: number, evaluation: EvaluationDoc) => {
    setSources((prevSources) => {
      const newSources = [...prevSources]
      newSources[courseIndex] = {
        evaluationId: evaluation.id,
        evaluationName: evaluation.saveName || '',
        ratingDate: evaluation.ratingRevision.ratingDate || '',
        manualVersion: evaluation.manualVersion || '',
        revision: evaluation.ratingRevision.ratingRevision || 0,
      }
      return newSources
    })
  }

  const getHoleInfo = (holeId: string, index: number) => {
    const course = courses?.find((course) =>
      course.course.holes.some((hole) => hole.physicalID === holeId)
    )
    const hole = course?.course.holes.find((hole) => hole.physicalID === holeId)
    return { course, hole: { ...hole, source: index } }
  }

  const holes = useMemo(() => template?.holeIds.map(getHoleInfo), [template])

  const totalPar = useMemo(
    () =>
      holes?.reduce((sum, h) => sum + parseInt(h.hole.par ?? '0', 10), 0) ?? 0,
    [holes]
  )

  const holeEvals = useMemo(() => {
    const holeEvals = holes?.map((hole) => {
      return {
        holeId: hole.hole.physicalID,
        evaluationId: hole.course?.source
          ? sources[hole.course.source - 1].evaluationId
          : undefined,
      }
    })
    return holeEvals
  }, [holes, sources])

  useEffect(() => {
    if (sources.length === 1 && !formRatingDate)
      setFormRatingDate(sources[0].ratingDate)
  }, [sources])

  const submit = async () => {
    try {
      if (sources.length === 0) return

      if (template?.name === undefined || template?.name === '') {
        openSnackbar(
          'Composite course does not have a name. Please provide one'
        )
        return
      }

      setIsGenerating(true)
      const { data } = await generateAxios.post(`/`, {
        clubId: club?.id,
        holes: holeEvals,
        name: template?.name,
        templateId: template?.id,
        clubNameOverride: clubName,
        courseNameOverride: courseName,
        ratingNameOverride: ratingName,
        dateOverride: formRatingDate,
        title: title,
        generatedBy: user?.email,
      })
      const downloadFolder = (ref: string) => {
        const doc = firebase.storage().ref(ref)
        doc.getDownloadURL().then((url) => {
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.download = document.title || ''
          anchor.click()
        })
      }

      downloadFolder(data)
      onClose()
    } catch (error) {
      console.error('Error generating form:', error)
      openSnackbar('Error generating form')
    } finally {
      setIsGenerating(false)
    }
  }

  const CourseDropdown = ({
    course,
    index,
  }: {
    course: SourceCourse
    index: number
  }) => {
    if (!sources[index]) return null

    return (
      <div
        key={'source' + course.source}
        style={{ marginRight: 8, width: courses?.length === 1 ? 564 : 280 }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>
              <span style={{ fontWeight: 500 }}>
                Select rating {course.source}
              </span>{' '}
              <span style={{ fontWeight: 600 }}>{course.course.name}</span>
            </span>
            {sources[index]?.evaluationId ? (
              <S.CheckContainer>
                <FeatherIcon name="check" size={14} color="white" />
              </S.CheckContainer>
            ) : (
              <FeatherIcon name="alert-circle" size={18} color="#e53935" />
            )}
          </div>
          <span style={{ fontSize: 12, fontWeight: 400, color: '#000' }}>
            Required
          </span>
        </div>
        <Spacer height={12} />
        <DropDownMenu
          yAdj={10}
          width={560}
          renderValue={() => (
            <span style={{ display: 'flex', width: '100%' }}>
              <span style={{ flexGrow: 1, textAlign: 'start' }}>
                {sources[index]?.evaluationId === ''
                  ? 'Select Rating'
                  : sources[index]?.evaluationName &&
                    sources[index].evaluationName.length > 0
                  ? sources[index].evaluationName
                  : 'Unnamed'}
              </span>
              <DateTimeView
                dateString={sources[index].ratingDate}
                showTime={false}
              />
            </span>
          )}
          relative
        >
          {course.evaluations.length ? (
            <>
              <S.Source
                style={{
                  pointerEvents: 'none',
                  fontWeight: 600,
                  fontSize: 11,
                  color: '#666',
                }}
              >
                <span style={{ flexGrow: 1, textAlign: 'start' }}>Name</span>
                <span style={{ width: 80, textAlign: 'end' }}>Rating date</span>
                <span style={{ width: 8 }} />
                <span style={{ width: 80, textAlign: 'end' }}>
                  Finalized at
                </span>
              </S.Source>
              {course.evaluations.map((evaluation) => (
                <S.Source
                  key={`evaluation-${evaluation.id}`}
                  onClick={() => updateSource(index, evaluation)}
                >
                  <span style={{ flexGrow: 1, textAlign: 'start' }}>
                    {evaluation.saveName === ''
                      ? 'Unnamed'
                      : evaluation.saveName}
                  </span>
                  <DateTimeView
                    dateString={evaluation?.ratingRevision.ratingDate}
                    showTime={false}
                  />
                  <span style={{ width: 8 }} />
                  <DateTimeView
                    dateString={evaluation?.finalizedAt ?? undefined}
                    showTime={false}
                  />
                </S.Source>
              ))}
            </>
          ) : (
            <S.Source>No Ratings available</S.Source>
          )}
        </DropDownMenu>
      </div>
    )
  }

  const RatingDateDropdown = () => {
    const isDisabled =
      sources.filter((source) => source.evaluationId === '').length > 0 ||
      sources.length <= 1
    return (
      <div
        style={{
          marginRight: 8,
          width: 280,
          opacity: isDisabled ? 0.5 : 1,
        }}
      >
        <Spacer height={8} />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontWeight: 500,
          }}
        >
          <span>Rating date:</span>
          {formRatingDate ? (
            <S.CheckContainer>
              <FeatherIcon name="check" size={14} color="white" />
            </S.CheckContainer>
          ) : (
            <FeatherIcon name="alert-circle" size={18} color="#e53935" />
          )}
        </div>
        <Spacer height={8} />
        <DropDownMenu
          disabledState={{
            disabled: isDisabled,
            message: 'Select Rating First',
          }}
          yAdj={10}
          width={280}
          value={
            formRatingDate !== ''
              ? new Date(formRatingDate).toLocaleDateString('sv-SE')
              : 'Select Date'
          }
          relative
        >
          {sources.length &&
            sources.map((source, i) => (
              <S.Source
                key={`source-${i}`}
                onClick={() => setFormRatingDate(source.ratingDate)}
              >
                <span style={{ flexGrow: 1, textAlign: 'start' }}>
                  {source.evaluationName}
                </span>
                <DateTimeView dateString={source.ratingDate} showTime={false} />
              </S.Source>
            ))}
        </DropDownMenu>
      </div>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="90vw" height="90vh">
      <ModalHeader>
        <S.Title>Generate Form</S.Title>
        <div style={{ display: 'block' }}>
          <SlopeTableHeaderPreview
            clubName={clubName}
            courseName={courseName}
            ratingName={ratingName}
            holeCount={holes?.length}
            totalPar={totalPar}
            revision={sources[0]?.revision}
            manualVersion={sources[0]?.manualVersion}
            ratingDate={formRatingDate || sources[0]?.ratingDate}
          />
        </div>
      </ModalHeader>
      <S.Content>
        {isGenerating ? (
          <S.SpinnerContainer>
            <Spinner />
            <S.SpinnerText> Generating...</S.SpinnerText>
          </S.SpinnerContainer>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: 4,
                alignItems: 'flex-end',
              }}
            >
              <div
                style={{
                  width: 280,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 22,
                  }}
                >
                  <label htmlFor="name" style={{ fontWeight: 500 }}>
                    Title
                  </label>
                </div>
                <div style={{ fontSize: 12 }}>
                  Name this form generation, for example: Preliminär värdering
                  or Final. <span style={{ color: '#212121' }}>Optional</span>
                </div>
                <S.Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              <div
                style={{
                  width: 280,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <label htmlFor="name" style={{ fontWeight: 500 }}>
                    Club name
                  </label>
                  {clubName ? (
                    <S.CheckContainer>
                      <FeatherIcon name="check" size={14} color="white" />
                    </S.CheckContainer>
                  ) : (
                    <span style={{ display: 'flex' }}>
                      <FeatherIcon
                        name="alert-circle"
                        size={18}
                        color="#e53935"
                      />
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12 }}>
                  Will appear in forms header.{' '}
                  <span style={{ color: '#212121' }}>Required</span>
                </div>
                <S.Input
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value.toUpperCase())}
                />
              </div>

              <div
                style={{
                  width: 280,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <label htmlFor="name" style={{ fontWeight: 500 }}>
                    Composite Course name
                  </label>
                  {courseName ? (
                    <S.CheckContainer>
                      <FeatherIcon name="check" size={14} color="white" />
                    </S.CheckContainer>
                  ) : (
                    <span style={{ display: 'flex' }}>
                      <FeatherIcon
                        name="alert-circle"
                        size={18}
                        color="#e53935"
                      />
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12 }}>
                  Will appear in forms header.{' '}
                  <span style={{ color: '#212121' }}>Required</span>
                </div>
                <S.Input
                  value={courseName}
                  onChange={(e) => setCourseName(e.target.value)}
                />
              </div>
            </div>
            <Spacer height={8} />
            <div style={{ display: 'flex', flexDirection: 'row', gap: 4 }}>
              <div
                style={{
                  width: 280,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 22,
                  }}
                >
                  <label htmlFor="name" style={{ fontWeight: 500 }}>
                    Rating name
                  </label>
                </div>
                <div style={{ fontSize: 12 }}>
                  Will appear in forms header.{' '}
                  <span style={{ color: '#212121' }}>Optional</span>
                </div>
                <S.Input
                  value={ratingName}
                  onChange={(e) => setRatingName(e.target.value)}
                />
              </div>

              <Spacer height={8} />

              <div style={{ display: 'flex' }}>
                {courses?.map((course, index) => (
                  <CourseDropdown
                    key={`course-${course.course.id}`}
                    course={course}
                    index={index}
                  />
                ))}
              </div>
            </div>
            <div>
              {true && (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <RatingDateDropdown />
                  <div style={{ fontSize: 12 }}>
                    If composite contains multiple courses.{' '}
                    <span style={{ color: '#212121' }}>Required</span>
                  </div>
                </div>
              )}
            </div>
            <div style={{ fontWeight: 500 }}>Holes</div>
            <S.HoleTableContainer>
              <S.HoleStyledTable>
                <S.HoleTableHead>
                  <tr>
                    <S.HoleTableHeaderCell style={{ width: 32 }} />
                    <S.HoleTableHeaderCell>#</S.HoleTableHeaderCell>
                    <S.HoleTableHeaderCell>Par</S.HoleTableHeaderCell>
                    <S.HoleTableHeaderCell>Course</S.HoleTableHeaderCell>
                    <S.HoleTableHeaderCell>Source Hole</S.HoleTableHeaderCell>
                  </tr>
                </S.HoleTableHead>
                <tbody>
                  {holes?.map((hole, holeIndex) => {
                    const isFilled = !!holeEvals?.[holeIndex]?.evaluationId
                    return (
                      <S.HoleTableRow
                        key={`hole-${hole.hole.id}`}
                        filled={isFilled}
                      >
                        <S.HoleTableCell>
                          {isFilled && (
                            <S.CheckContainer>
                              <FeatherIcon
                                name="check"
                                size={14}
                                color="white"
                              />
                            </S.CheckContainer>
                          )}
                        </S.HoleTableCell>
                        <S.HoleTableCell>{holeIndex + 1}</S.HoleTableCell>
                        <S.HoleTableCell>{hole.hole.par}</S.HoleTableCell>
                        <S.HoleTableCell>
                          {hole.course?.course.name ?? '—'}
                        </S.HoleTableCell>
                        <S.HoleTableCell>
                          {hole.hole.courseHoleNumber ?? '—'}
                        </S.HoleTableCell>
                      </S.HoleTableRow>
                    )
                  })}
                </tbody>
              </S.HoleStyledTable>
            </S.HoleTableContainer>
          </>
        )}
      </S.Content>
      <ModalFooter>
        <RoundedButton
          disabled={!holeEvals || holeEvals.some((h) => !h.evaluationId)}
          title="Generate forms"
          width={'100%'}
          onPress={submit}
          bgColor="primary2"
        />
      </ModalFooter>
    </Modal>
  )
}

export default MixedEvaluationModal
